import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel,
  MenuItem, Select, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createDashboard, listDashboards, pinChatTurn } from '../../../utilityFunctions/dashboardApi';

const NEW = '__new__';

/**
 * Pin a completed chat turn to a dashboard: POST /{id}/pin-chat-turn { chat_turn_id, query_index }.
 * The picker lists the user's dashboards; "New dashboard…" creates one first (POST /).
 *
 * @param queries  the turn's supporting queries (answer.supporting_queries) — picks query_index
 */
export default function PinToDashboardDialog({ open, onClose, jwt, chatTurnId, queries }) {
  const { t } = useTranslation();
  const history = useHistory();
  const [dashboards, setDashboards] = React.useState(null);
  const [target, setTarget] = React.useState('');
  const [newTitle, setNewTitle] = React.useState('');
  const [queryIndex, setQueryIndex] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [pinned, setPinned] = React.useState(null); // { id, title }

  const queryList = Array.isArray(queries) ? queries : [];

  React.useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setError(null);
    setPinned(null);
    setQueryIndex(0);
    setDashboards(null);
    listDashboards(jwt).then((res) => {
      if (!alive) return;
      const list = res.ok && Array.isArray(res.data) ? res.data : [];
      setDashboards(list);
      setTarget(list.length ? String(list[0].id) : NEW);
      if (!res.ok) setError(t('Dashboards.list.loadFailed'));
    });
    return () => { alive = false; };
  }, [open, jwt, t]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    let dashboardId = target;
    let title = ((dashboards || []).find((d) => String(d.id) === String(target)) || {}).title;
    if (target === NEW) {
      const name = newTitle.trim();
      const created = await createDashboard(name, jwt);
      const d = created.ok && created.data && (created.data.dashboard || created.data);
      if (!d || d.id == null) {
        setBusy(false);
        setError(t('Dashboards.list.createFailed'));
        return;
      }
      dashboardId = d.id;
      title = d.title || name;
    }
    const res = await pinChatTurn(dashboardId, chatTurnId, queryIndex, jwt);
    setBusy(false);
    if (res.ok) {
      setPinned({ id: dashboardId, title });
      if (target === NEW) setNewTitle('');
    } else {
      setError((res.data && (res.data.message || res.data.error)) || t('Dashboards.pin.failed'));
    }
  };

  const canSubmit = !busy && dashboards !== null && (target !== NEW || newTitle.trim().length > 0);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('Dashboards.pin.title')}</DialogTitle>
      <DialogContent sx={{ overflowWrap: 'anywhere' }}>
        {pinned ? (
          <Alert severity="success" sx={{ mt: 1 }}>{t('Dashboards.pin.pinned', { title: pinned.title })}</Alert>
        ) : (
          <>
            {dashboards === null && <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', my: 2 }} />}
            {dashboards !== null && (
              <>
                {dashboards.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('Dashboards.pin.noDashboards')}</Typography>
                )}
                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel id="pin-dash-label">{t('Dashboards.pin.pickDashboard')}</InputLabel>
                  <Select labelId="pin-dash-label" label={t('Dashboards.pin.pickDashboard')} value={target}
                    onChange={(e) => setTarget(e.target.value)} disabled={busy}>
                    {dashboards.map((d) => <MenuItem key={d.id} value={String(d.id)}>{d.title}</MenuItem>)}
                    <MenuItem value={NEW}><em>{t('Dashboards.pin.newDashboard')}</em></MenuItem>
                  </Select>
                </FormControl>
                {target === NEW && (
                  <TextField autoFocus fullWidth margin="dense" size="small" label={t('Dashboards.pin.newTitleLabel')}
                    value={newTitle} onChange={(e) => setNewTitle(e.target.value)} disabled={busy} inputProps={{ maxLength: 200 }} />
                )}
                {queryList.length > 1 && (
                  <FormControl fullWidth margin="dense" size="small">
                    <InputLabel id="pin-query-label">{t('Dashboards.pin.queryLabel')}</InputLabel>
                    <Select labelId="pin-query-label" label={t('Dashboards.pin.queryLabel')} value={queryIndex}
                      onChange={(e) => setQueryIndex(Number(e.target.value))} disabled={busy}>
                      {queryList.map((q, i) => (
                        <MenuItem key={i} value={i}>{(q && q.metric) || t('Dashboards.pin.query', { n: i + 1 })}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{t('Dashboards.pin.staticNote')}</Typography>
              </>
            )}
          </>
        )}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{pinned ? t('Dashboards.common.close') : t('Dashboards.common.cancel')}</Button>
        {pinned ? (
          <Button variant="contained" onClick={() => history.push(`/data_analytics/dashboards/${pinned.id}`)}>
            {t('Dashboards.pin.openDashboard')}
          </Button>
        ) : (
          <Button variant="contained" onClick={submit} disabled={!canSubmit}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
            {t('Dashboards.pin.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
